"use client"
import { useState, useMemo, useEffect } from "react"
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  Switch,
  useDisclosure,
  useToast,
  FormControl,
  FormLabel,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  Textarea,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  IconButton,
  Container,
  InputGroup,
  InputLeftElement,
  TableContainer,
  Checkbox,
} from "@chakra-ui/react"
import { AddIcon, ViewIcon, EditIcon, DeleteIcon, SearchIcon } from "@chakra-ui/icons"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchSubscriptionasync,
  selectSubscription,
  addSubscriptionasync,
  updateSubscriptionasync,
  deleteSubscriptionasync,
} from "../../Subscriptions/SubscriptionDetails/SubscriptionSlice"

const initialPlanState = {
  type: "",
  is_default: false,
  title: "",
  description: "",
  subscription_type: "",
  mrp_amount: "",
  amount: "",
  sort_order: "",
  subscription_feature: [],
  validity: "",
  validity_type: "day",
  status: "active",
  profile_creation: false,
  no_of_job_apply: 0,
  no_of_resume_builder_apply: 0,
  resume_builder: false,
  notification_via_email_sms_whatsapp: false,
  no_of_job_post: 0,
  no_of_urgent_hiring_tag: 0,
  no_of_candidate_unlock: 0,
  no_of_job_published: 0,
  job_boosts: false,
  smart_boost_via_email_sms_whatsapp: false,
  dedicated_relationship_manager: false,
  campion_via_email_sms_whatsapp: false,
  multiple_user_login: false,
  reports: false,
  company_branding: false,
  job_applicants_download: false,
  unlocked_candidate_download: false,
}

const CreatePlan = () => {
  const dispatch = useDispatch()
  const subscriptions = useSelector(selectSubscription)
  const [currentPlan, setCurrentPlan] = useState(initialPlanState)
  const [selectedPlans, setSelectedPlans] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [userTypeFilter, setUserTypeFilter] = useState("all")
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState("all")
  const [errors, setErrors] = useState({})
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const [isLoading, setIsLoading] = useState(true)
  const toast = useToast()

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        await dispatch(fetchSubscriptionasync()).unwrap()
      } catch (error) {
        toast({
          title: "Failed to load subscription plans.",
          description: error.toString(),
          status: "error",
          duration: 3000,
          isClosable: true,
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [dispatch, toast])

  useEffect(() => {
    if (!subscriptions || subscriptions.length === 0) {
      dispatch(fetchSubscriptionasync())
    }
  }, [dispatch, subscriptions])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setCurrentPlan({ ...currentPlan, [name]: value })
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
  }

  const handleSwitchChange = (name) => {
    setCurrentPlan({ ...currentPlan, [name]: !currentPlan[name] })
  }

  const handleNumberInputChange = (name, value) => {
    // Chakra's NumberInput onChange provides the value directly
    const newValue = typeof value === 'string' ? value : value.target.value;
    setCurrentPlan({ ...currentPlan, [name]: newValue });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...currentPlan.subscription_feature]
    updatedFeatures[index] = { ...updatedFeatures[index], [field]: value }
    setCurrentPlan({ ...currentPlan, subscription_feature: updatedFeatures })
  }

  const addFeature = () => {
    setCurrentPlan({
      ...currentPlan,
      subscription_feature: [...currentPlan.subscription_feature, { feature: "", sort_order: "" }],
    })
  }

  const removeFeature = (index) => {
    const updatedFeatures = currentPlan.subscription_feature.filter((_, i) => i !== index)
    setCurrentPlan({ ...currentPlan, subscription_feature: updatedFeatures })
  }

  const validateForm = () => {
    const newErrors = {}
    if (!currentPlan.type) newErrors.type = "User type is required."
    if (!currentPlan.title.trim()) newErrors.title = "Title is required."
    if (!currentPlan.description.trim()) newErrors.description = "Description is required."
    if (!currentPlan.subscription_type) newErrors.subscription_type = "Subscription type is required."
    if (currentPlan.mrp_amount === "" || isNaN(parseFloat(currentPlan.mrp_amount)))
      newErrors.mrp_amount = "Valid MRP amount is required."
    if (currentPlan.amount === "" || isNaN(parseFloat(currentPlan.amount))) newErrors.amount = "Valid amount is required."
    if (currentPlan.sort_order === "" || isNaN(parseInt(currentPlan.sort_order, 10)))
      newErrors.sort_order = "Valid sort order is required."
    if (currentPlan.validity === "" || isNaN(parseInt(currentPlan.validity, 10)))
      newErrors.validity = "Valid validity is required."

    if (currentPlan.subscription_feature.length === 0) {
      newErrors.subscription_feature = "At least one feature is required."
    } else {
      currentPlan.subscription_feature.forEach((feature, index) => {
        if (!feature.feature.trim()) {
          newErrors[`feature_${index}_feature`] = "Feature description cannot be empty."
        }
        if (feature.sort_order === "" || isNaN(parseInt(feature.sort_order, 10))) {
          newErrors[`feature_${index}_sort_order`] = "A valid sort order is required."
        }
      })
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const preparePayload = (planData) => ({
    ...planData,
    mrp_amount: Number.parseFloat(planData.mrp_amount),
    amount: Number.parseFloat(planData.amount),
    sort_order: Number.parseInt(planData.sort_order, 10),
    validity: Number.parseInt(planData.validity, 10),
    no_of_job_apply: Number.parseInt(planData.no_of_job_apply, 10),
    no_of_resume_builder_apply: Number.parseInt(planData.no_of_resume_builder_apply, 10),
    no_of_job_post: Number.parseInt(planData.no_of_job_post, 10),
    no_of_urgent_hiring_tag: Number.parseInt(planData.no_of_urgent_hiring_tag, 10),
    no_of_candidate_unlock: Number.parseInt(planData.no_of_candidate_unlock, 10),
    no_of_job_published: Number.parseInt(planData.no_of_job_published, 10),
    subscription_feature: planData.subscription_feature.map((f) => ({
      ...f,
      sort_order: Number.parseInt(f.sort_order, 10),
    })),
    resume_builder: false,
  })

  const handleAddPlan = async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields correctly.",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      const newPlanPayload = preparePayload(currentPlan)
      await dispatch(addSubscriptionasync(newPlanPayload)).unwrap()
      toast({
        title: "Plan added successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      })
      onAddClose()
      dispatch(fetchSubscriptionasync())
    } catch (error) {
      toast({
        title: "Failed to add plan.",
        description: error.toString(),
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleUpdatePlan = async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields correctly.",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      const updatedPlanPayload = preparePayload(currentPlan)
      await dispatch(
        updateSubscriptionasync({
          id: currentPlan.uuid,
          updateSubscriptiondata: updatedPlanPayload,
        }),
      ).unwrap()
      toast({
        title: "Plan updated successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      })
      onEditClose()
      dispatch(fetchSubscriptionasync())
    } catch (error) {
      toast({
        title: "Failed to update plan.",
        description: error.toString(),
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleDeletePlan = async () => {
    try {
      await dispatch(deleteSubscriptionasync(currentPlan.uuid)).unwrap()
      toast({
        title: "Plan deleted successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      })
      onDeleteClose()
      dispatch(fetchSubscriptionasync())
    } catch (error) {
      toast({
        title: "Failed to delete plan.",
        description: error.toString(),
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // --- MODIFIED FUNCTION FOR PRE-FILLING ---
  const handleEditPlan = (plan) => {
    // Create a new object to avoid mutating the Redux state directly.
    // Merge with initial state to ensure all keys exist.
    const planForEditing = { ...initialPlanState, ...plan };

    // Explicitly convert all fields bound to inputs to strings, handling null/undefined.
    // This ensures controlled components receive the correct value type.
    const stringifiedPlan = {
      ...planForEditing,
      mrp_amount: String(planForEditing.mrp_amount || ''),
      amount: String(planForEditing.amount || ''),
      sort_order: String(planForEditing.sort_order || ''),
      validity: String(planForEditing.validity || ''),
      no_of_job_apply: String(planForEditing.no_of_job_apply || 0),
      no_of_resume_builder_apply: String(planForEditing.no_of_resume_builder_apply || 0),
      no_of_job_post: String(planForEditing.no_of_job_post || 0),
      no_of_urgent_hiring_tag: String(planForEditing.no_of_urgent_hiring_tag || 0),
      no_of_candidate_unlock: String(planForEditing.no_of_candidate_unlock || 0),
      no_of_job_published: String(planForEditing.no_of_job_published || 0),
      // Ensure features are an array and sort_order is a string
      subscription_feature: (planForEditing.subscription_feature || []).map(f => ({
        ...f,
        sort_order: String(f.sort_order || '')
      })),
      // Ensure booleans are actual booleans for Switches
      profile_creation: !!planForEditing.profile_creation,
      notification_via_email_sms_whatsapp: !!planForEditing.notification_via_email_sms_whatsapp,
      job_boosts: !!planForEditing.job_boosts,
      smart_boost_via_email_sms_whatsapp: !!planForEditing.smart_boost_via_email_sms_whatsapp,
      dedicated_relationship_manager: !!planForEditing.dedicated_relationship_manager,
      campion_via_email_sms_whatsapp: !!planForEditing.campion_via_email_sms_whatsapp,
      multiple_user_login: !!planForEditing.multiple_user_login,
      reports: !!planForEditing.reports,
      company_branding: !!planForEditing.company_branding,
      job_applicants_download: !!planForEditing.job_applicants_download,
      unlocked_candidate_download: !!planForEditing.unlocked_candidate_download,
    };

    setCurrentPlan(stringifiedPlan);
    setErrors({}); // Clear any previous validation errors
    onEditOpen();
  };

  const handleDeleteConfirmation = (planId) => {
    const planToDelete = subscriptions.find((plan) => plan.uuid === planId)
    if (planToDelete) {
      setCurrentPlan(planToDelete)
      onDeleteOpen()
    }
  }

  const handleViewPlan = (plan) => {
    setCurrentPlan(plan)
    onViewOpen()
  }

  const filteredPlans = useMemo(() => {
    if (!subscriptions || !Array.isArray(subscriptions) || subscriptions.length === 0) {
      return []
    }
    return subscriptions.filter((plan) => {
      if (!plan) return false
      const nameMatch =
        plan.title && typeof plan.title === "string"
          ? plan.title.toLowerCase().includes((searchTerm || "").toLowerCase())
          : false
      const userTypeMatch = userTypeFilter === "all" || plan.type === userTypeFilter
      const subscriptionTypeMatch =
        subscriptionTypeFilter === "all" || plan.subscription_type === subscriptionTypeFilter
      return nameMatch && userTypeMatch && subscriptionTypeMatch
    })
  }, [subscriptions, searchTerm, userTypeFilter, subscriptionTypeFilter])

  const toggleSelectAll = () => {
    if (selectedPlans.length === filteredPlans.length && filteredPlans.length > 0) {
      setSelectedPlans([])
    } else {
      setSelectedPlans(filteredPlans.map((plan) => plan.uuid))
    }
  }

  const togglePlanSelection = (planId) => {
    if (selectedPlans.includes(planId)) {
      setSelectedPlans(selectedPlans.filter((id) => id !== planId))
    } else {
      setSelectedPlans([...selectedPlans, planId])
    }
  }

  const renderPlanForm = () => (
    <VStack spacing={4}>
      <FormControl isInvalid={!!errors.type}>
        <FormLabel>Type</FormLabel>
        <Select name="type" placeholder="Select user type" value={currentPlan.type || ""} onChange={handleInputChange}>
          <option value="employer">Employer</option>
          <option value="candidate">Candidate</option>
        </Select>
        <FormErrorMessage>{errors.type}</FormErrorMessage>
      </FormControl>
      <FormControl>
        <FormLabel>Is Default</FormLabel>
        <Switch isChecked={false} isDisabled={true} />
      </FormControl>
      <FormControl isInvalid={!!errors.title}>
        <FormLabel>Title</FormLabel>
        <Input name="title" value={currentPlan.title} onChange={handleInputChange} placeholder="e.g. Gold Plan" />
        <FormErrorMessage>{errors.title}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.description}>
        <FormLabel>Description</FormLabel>
        <Textarea name="description" value={currentPlan.description} onChange={handleInputChange} />
        <FormErrorMessage>{errors.description}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.subscription_type}>
        <FormLabel>Subscription Type</FormLabel>
        <Select
          name="subscription_type"
          placeholder="Select subscription type"
          value={currentPlan.subscription_type}
          onChange={handleInputChange}
        >
          <option value="Premium">Premium</option>
          <option value="Basic">Basic</option>
          <option value="Standard">Standard</option>
          <option value="Custom">Custom</option>
        </Select>
        <FormErrorMessage>{errors.subscription_type}</FormErrorMessage>
      </FormControl>
      <HStack width="100%">
        <FormControl isInvalid={!!errors.mrp_amount}>
          <FormLabel>MRP Amount</FormLabel>
          <NumberInput min={0} value={currentPlan.mrp_amount} onChange={(valueAsString) => handleNumberInputChange("mrp_amount", valueAsString)}>
            <NumberInputField name="mrp_amount" />
          </NumberInput>
          <FormErrorMessage>{errors.mrp_amount}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.amount}>
          <FormLabel>Amount</FormLabel>
          <NumberInput min={0} value={currentPlan.amount} onChange={(valueAsString) => handleNumberInputChange("amount", valueAsString)}>
            <NumberInputField name="amount" />
          </NumberInput>
          <FormErrorMessage>{errors.amount}</FormErrorMessage>
        </FormControl>
      </HStack>
      <FormControl isInvalid={!!errors.sort_order}>
        <FormLabel>Sort Order</FormLabel>
        <NumberInput min={1} value={currentPlan.sort_order} onChange={(valueAsString) => handleNumberInputChange("sort_order", valueAsString)}>
          <NumberInputField name="sort_order" />
        </NumberInput>
        <FormErrorMessage>{errors.sort_order}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.subscription_feature}>
        <FormLabel>Subscription Features (Add at least one feature)</FormLabel>
        {currentPlan.subscription_feature.map((feature, index) => (
          <HStack key={index} mt={2} alignItems="flex-start">
            <FormControl isInvalid={!!errors[`feature_${index}_feature`]}>
              <Input
                placeholder="Feature Description"
                value={feature.feature}
                onChange={(e) => handleFeatureChange(index, "feature", e.target.value)}
              />
              <FormErrorMessage>{errors[`feature_${index}_feature`]}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors[`feature_${index}_sort_order`]}>
              <NumberInput min={1} value={feature.sort_order} onChange={(valueAsString) => handleFeatureChange(index, "sort_order", valueAsString)}>
                <NumberInputField
                  placeholder="Sort Order"
                />
              </NumberInput>
              <FormErrorMessage>{errors[`feature_${index}_sort_order`]}</FormErrorMessage>
            </FormControl>
            <Button onClick={() => removeFeature(index)} colorScheme="red">
              Remove
            </Button>
          </HStack>
        ))}
        <Button onClick={addFeature} mt={2} size="sm">
          Add Feature
        </Button>
        <FormErrorMessage>{errors.subscription_feature}</FormErrorMessage>
      </FormControl>
      <HStack width="100%">
        <FormControl isInvalid={!!errors.validity}>
          <FormLabel>Validity</FormLabel>
          <NumberInput min={1} value={currentPlan.validity} onChange={(valueAsString) => handleNumberInputChange("validity", valueAsString)}>
            <NumberInputField name="validity" />
          </NumberInput>
          <FormErrorMessage>{errors.validity}</FormErrorMessage>
        </FormControl>
        <FormControl>
          <FormLabel>Validity Type</FormLabel>
          <Select name="validity_type" value={currentPlan.validity_type} onChange={handleInputChange}>
            <option value="day">Day</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </Select>
        </FormControl>
      </HStack>
      <FormControl>
        <FormLabel>Status</FormLabel>
        <Select name="status" value={currentPlan.status} onChange={handleInputChange}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </FormControl>

      {currentPlan.type === "candidate" && (
        <>
          <FormControl>
            <FormLabel>Profile Creation</FormLabel>
            <Switch isChecked={currentPlan.profile_creation} onChange={() => handleSwitchChange("profile_creation")} />
          </FormControl>
          <FormControl>
            <FormLabel>Notification via Email/SMS/WhatsApp</FormLabel>
            <Switch
              isChecked={currentPlan.notification_via_email_sms_whatsapp}
              onChange={() => handleSwitchChange("notification_via_email_sms_whatsapp")}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Number of Job Applications</FormLabel>
            <NumberInput min={0} value={currentPlan.no_of_job_apply}
              onChange={(val) => handleNumberInputChange("no_of_job_apply", val)}>
              <NumberInputField name="no_of_job_apply" />
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>Number of Resume Builder Apply/Use</FormLabel>
            <NumberInput min={0} value={currentPlan.no_of_resume_builder_apply}
              onChange={(val) => handleNumberInputChange("no_of_resume_builder_apply", val)}>
              <NumberInputField name="no_of_resume_builder_apply" />
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>Resume Builder</FormLabel>
            <Switch isChecked={false} isDisabled={true} />
          </FormControl>
        </>
      )}
      {currentPlan.type === "employer" && (
        <>
          <FormControl>
            <FormLabel>Number of Job Posts</FormLabel>
            <NumberInput min={0} value={currentPlan.no_of_job_post}
              onChange={(val) => handleNumberInputChange("no_of_job_post", val)}>
              <NumberInputField name="no_of_job_post" />
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>Number of Urgent Hiring Tags</FormLabel>
            <NumberInput min={0} value={currentPlan.no_of_urgent_hiring_tag}
              onChange={(val) => handleNumberInputChange("no_of_urgent_hiring_tag", val)}>
              <NumberInputField name="no_of_urgent_hiring_tag" />
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>Number of Candidate Unlocks</FormLabel>
            <NumberInput min={0} value={currentPlan.no_of_candidate_unlock}
              onChange={(val) => handleNumberInputChange("no_of_candidate_unlock", val)}>
              <NumberInputField name="no_of_candidate_unlock" />
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>Number of Job Published</FormLabel>
            <NumberInput min={0} value={currentPlan.no_of_job_published}
              onChange={(val) => handleNumberInputChange("no_of_job_published", val)}>
              <NumberInputField name="no_of_job_published" />
            </NumberInput>
          </FormControl>
          <FormControl><FormLabel>Job Boosts</FormLabel><Switch isChecked={currentPlan.job_boosts} onChange={() => handleSwitchChange("job_boosts")} /></FormControl>
          <FormControl><FormLabel>Smart Boost via Email/SMS/WhatsApp</FormLabel><Switch isChecked={currentPlan.smart_boost_via_email_sms_whatsapp} onChange={() => handleSwitchChange("smart_boost_via_email_sms_whatsapp")} /></FormControl>
          <FormControl><FormLabel>Dedicated Relationship Manager</FormLabel><Switch isChecked={currentPlan.dedicated_relationship_manager} onChange={() => handleSwitchChange("dedicated_relationship_manager")} /></FormControl>
          <FormControl><FormLabel>Campaign via Email/SMS/WhatsApp</FormLabel><Switch isChecked={currentPlan.campion_via_email_sms_whatsapp} onChange={() => handleSwitchChange("campion_via_email_sms_whatsapp")} /></FormControl>
          <FormControl><FormLabel>Multiple User Login</FormLabel><Switch isChecked={currentPlan.multiple_user_login} onChange={() => handleSwitchChange("multiple_user_login")} /></FormControl>
          <FormControl><FormLabel>Reports</FormLabel><Switch isChecked={currentPlan.reports} onChange={() => handleSwitchChange("reports")} /></FormControl>
          <FormControl><FormLabel>Company Branding</FormLabel><Switch isChecked={currentPlan.company_branding} onChange={() => handleSwitchChange("company_branding")} /></FormControl>
          <FormControl><FormLabel>Job Applicants Download</FormLabel><Switch isChecked={currentPlan.job_applicants_download} onChange={() => handleSwitchChange("job_applicants_download")} /></FormControl>
          <FormControl><FormLabel>Unlocked Candidate Download</FormLabel><Switch isChecked={currentPlan.unlocked_candidate_download} onChange={() => handleSwitchChange("unlocked_candidate_download")} /></FormControl>
        </>
      )}
    </VStack>
  )

  return (
    <div className=" bg-adminbg rounded " style={{ fontFamily: "Inter, sans-serif" }}>
      <Container maxW="container.2xl" className="bg-white my-2 rounded-md">
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <h2 className="text-xl text-black font-semibold px-3 py-7">Subscription Plans</h2>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => {
              setCurrentPlan(initialPlanState)
              setErrors({})
              onAddOpen()
            }}
          >
            Add New Plan
          </Button>
        </Flex>
        <VStack spacing={4} mb={8}>
          <HStack width="100%" spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input placeholder="Search plans..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </InputGroup>
            <Select value={userTypeFilter} onChange={(e) => setUserTypeFilter(e.target.value)}>
              <option value="all">All User Types</option>
              <option value="employer">Employer</option>
              <option value="candidate">Candidate</option>
            </Select>
            <Select value={subscriptionTypeFilter} onChange={(e) => setSubscriptionTypeFilter(e.target.value)}>
              <option value="all">All Subscription Types</option>
              <option value="Premium">Premium</option>
              <option value="Basic">Basic</option>
              <option value="Standard">Standard</option>
              <option value="Custom">Custom</option>
            </Select>
          </HStack>
        </VStack>
        <TableContainer>
          <Table colorScheme="gray" size="sm">
            <Thead className="bg-gray-200">
              <Tr>
                <Th width="40px">
                  <Checkbox
                    isChecked={selectedPlans.length === filteredPlans.length && filteredPlans.length > 0}
                    onChange={toggleSelectAll}
                    isDisabled={filteredPlans.length === 0}
                  />
                </Th>
                <Th>Title</Th>
                <Th>Type</Th>
                <Th>Subscription</Th>
                <Th>Amount</Th>
                <Th>Validity</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredPlans.map((plan) => (
                <Tr key={plan.uuid}>
                  <Td>
                    <Checkbox
                      isChecked={selectedPlans.includes(plan.uuid)}
                      onChange={() => togglePlanSelection(plan.uuid)}
                    />
                  </Td>
                  <Td fontWeight="medium">
                    {plan.title}
                    {plan.is_default == 1 && (
                      <Badge ml={2} colorScheme="yellow">
                        Default
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={plan.type === "employer" ? "green" : "purple"}>{plan.type}</Badge>
                  </Td>
                  <Td>{plan.subscription_type}</Td>
                  <Td>
                    ₹{plan.amount}
                    <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                      <s>₹{plan.mrp_amount}</s>
                    </Text>
                  </Td>
                  <Td>
                    {plan.validity} {plan.validity_type}
                    {plan.validity != 1 ? "s" : ""}
                  </Td>
                  <Td>
                    <Badge colorScheme={plan.status === "active" ? "green" : "red"}>{plan.status}</Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View plan"
                        icon={<ViewIcon />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => handleViewPlan(plan)}
                      />
                      {plan.is_default != 1 && (
                        <IconButton
                          aria-label="Edit plan"
                          icon={<EditIcon />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => handleEditPlan(plan)}
                        />
                      )}

                      {plan.is_default != 1 && (
                        <IconButton
                          aria-label="Delete plan"
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDeleteConfirmation(plan.uuid)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {/* Add/Edit Modals */}
        <Modal isOpen={isAddOpen} onClose={onAddClose} size="5xl" isCentered>
          <ModalOverlay />
          <ModalContent maxHeight={"80vh"}>
            <ModalHeader>Add New Plan</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto">{renderPlanForm()}</ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onAddClose}>Cancel</Button>
              <Button colorScheme="blue" onClick={handleAddPlan}>Add Plan</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal isOpen={isEditOpen} onClose={onEditClose} size="5xl" isCentered>
          <ModalOverlay />
          <ModalContent maxHeight={"80vh"}>
            <ModalHeader>Edit Plan</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto">{renderPlanForm()}</ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onEditClose}>Cancel</Button>
              <Button colorScheme="blue" onClick={handleUpdatePlan}>Update Plan</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {/* Delete Modal */}
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Plan</ModalHeader>
            <ModalCloseButton />
            <ModalBody>Are you sure you want to delete the plan "{currentPlan.title}"?</ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDeletePlan}>Delete</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {/* View Modal */}
        <Modal isOpen={isViewOpen} onClose={onViewClose} size="3xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>View Plan Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto" maxHeight="calc(100vh - 200px)">
              <VStack align="start" spacing={3}>
                <Text><strong>Title:</strong> {currentPlan.title}</Text>
                <Text><strong>Type:</strong> <Badge colorScheme={currentPlan.type === "employer" ? "green" : "purple"}>{currentPlan.type}</Badge></Text>
                <Text><strong>Description:</strong> {currentPlan.description}</Text>
                <Text><strong>Subscription Type:</strong> {currentPlan.subscription_type}</Text>
                <Text><strong>Amount:</strong> ₹{currentPlan.amount} (<s>₹{currentPlan.mrp_amount}</s>)</Text>
                <Text><strong>Validity:</strong> {currentPlan.validity} {currentPlan.validity_type}{currentPlan.validity != 1 ? "s" : ""}</Text>
                <Text><strong>Status:</strong> <Badge colorScheme={currentPlan.status === "active" ? "green" : "red"}>{currentPlan.status}</Badge></Text>
                <Text><strong>Features:</strong></Text>
                <VStack align="start" pl={4} spacing={1}>
                  {currentPlan.subscription_feature?.map((feature, index) => (<Text key={index}>• {feature.feature}</Text>))}
                </VStack>
                {currentPlan.type === "employer" ? (
                  <>
                    <Text pt={2}><strong>Employer Features:</strong></Text>
                    <VStack align="start" pl={4} spacing={1}>
                      <Text><strong>Job Posts:</strong> {currentPlan.no_of_job_post}</Text>
                      <Text><strong>Urgent Hiring Tags:</strong> {currentPlan.no_of_urgent_hiring_tag}</Text>
                      <Text><strong>Candidate Unlocks:</strong> {currentPlan.no_of_candidate_unlock}</Text>
                      <Text><strong>Jobs Published:</strong> {currentPlan.no_of_job_published}</Text>
                      <Text><strong>Job Boosts:</strong> {currentPlan.job_boosts ? "Yes" : "No"}</Text>
                      <Text><strong>Smart Boost:</strong> {currentPlan.smart_boost_via_email_sms_whatsapp ? "Yes" : "No"}</Text>
                      <Text><strong>Dedicated Manager:</strong> {currentPlan.dedicated_relationship_manager ? "Yes" : "No"}</Text>
                      <Text><strong>Campaigns:</strong> {currentPlan.campion_via_email_sms_whatsapp ? "Yes" : "No"}</Text>
                      <Text><strong>Multiple User Login:</strong> {currentPlan.multiple_user_login ? "Yes" : "No"}</Text>
                      <Text><strong>Reports:</strong> {currentPlan.reports ? "Yes" : "No"}</Text>
                      <Text><strong>Company Branding:</strong> {currentPlan.company_branding ? "Yes" : "No"}</Text>
                      <Text><strong>Job Applicants Download:</strong> {currentPlan.job_applicants_download ? "Yes" : "No"}</Text>
                      <Text><strong>Unlocked Candidate Download:</strong> {currentPlan.unlocked_candidate_download ? "Yes" : "No"}</Text>
                    </VStack>
                  </>
                ) : (
                  <>
                    <Text pt={2}><strong>Candidate Features:</strong></Text>
                    <VStack align="start" pl={4} spacing={1}>
                      <Text><strong>Profile Creation:</strong> {currentPlan.profile_creation ? "Yes" : "No"}</Text>
                      <Text><strong>Job Applications:</strong> {currentPlan.no_of_job_apply}</Text>
                      <Text><strong>Resume Builder Uses:</strong> {currentPlan.no_of_resume_builder_apply}</Text>
                      <Text><strong>Resume Builder Access:</strong> {currentPlan.resume_builder ? "Yes" : "No"}</Text>
                      <Text><strong>Notifications:</strong> {currentPlan.notification_via_email_sms_whatsapp ? "Yes" : "No"}</Text>
                    </VStack>
                  </>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter><Button colorScheme="blue" onClick={onViewClose}>Close</Button></ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </div>
  )
}

export default CreatePlan